import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import MatchActivityFeed from './components/MatchActivityFeed';
import MetricsPanel from './components/MetricsPanel';
import FlightTrackingModule from './components/FlightTrackingModule';
import EmergencyEventsPanel from './components/EmergencyEventsPanel';
import AIReasoningLogs from './components/AIReasoningLogs';

const AIMonitoringDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    region: '',
    timeRange: '24h',
    confidenceThreshold: 0.6,
    severity: ''
  });
  const [dashboardData, setDashboardData] = useState({
    matchingLogs: [],
    emergencyEvents: [],
    activeDeals: [],
    flightInfo: [],
    metrics: {
      successRate: 0,
      pendingMatches: 0,
      activePairings: 0,
      avgMatchScore: 0
    }
  });
  const [realtimeChannels, setRealtimeChannels] = useState([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      setupRealtimeSubscriptions();
    }

    return () => {
      cleanupRealtimeSubscriptions();
    };
  }, [user, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const timeRangeHours = {
        '1h': 1,
        '24h': 24,
        '7d': 168,
        '30d': 720
      };

      const hoursAgo = timeRangeHours?.[filters?.timeRange] || 24;
      const timeThreshold = new Date(Date.now() - hoursAgo * 60 * 60 * 1000)?.toISOString();

      const [matchingLogsRes, emergencyEventsRes, activeDealsRes, flightInfoRes] = await Promise.all([
        supabase
          ?.from('ai_matching_logs')
          ?.select('*')
          ?.gte('created_at', timeThreshold)
          ?.gte('match_score', filters?.confidenceThreshold)
          ?.order('created_at', { ascending: false })
          ?.limit(50),
        supabase
          ?.from('emergency_rematch_events')
          ?.select('*')
          ?.gte('created_at', timeThreshold)
          ?.order('created_at', { ascending: false })
          ?.limit(30),
        supabase
          ?.from('deals')
          ?.select(`
            *,
            buyer:buyer_id(id, full_name, avatar_url),
            traveler:traveler_id(id, full_name, avatar_url)
          `)
          ?.eq('is_ai_matched', true)
          ?.in('status', ['ai_matched', 'accepted', 'paid', 'in_transit'])
          ?.order('created_at', { ascending: false }),
        supabase
          ?.from('flight_information')
          ?.select(`
            *,
            trip:trip_id(id, from_country, to_country)
          `)
          ?.in('flight_status', ['delayed', 'cancelled', 'in_flight'])
          ?.order('updated_at', { ascending: false })
          ?.limit(20)
      ]);

      const matchingLogs = matchingLogsRes?.data || [];
      const emergencyEvents = emergencyEventsRes?.data || [];
      const activeDeals = activeDealsRes?.data || [];
      const flightInfo = flightInfoRes?.data || [];

      const totalMatches = matchingLogs?.length;
      const successfulMatches = matchingLogs?.filter(log => log?.match_score >= 0.7)?.length;
      const successRate = totalMatches > 0 ? (successfulMatches / totalMatches * 100) : 0;
      const pendingMatches = activeDeals?.filter(d => d?.status === 'ai_matched')?.length;
      const activePairings = activeDeals?.filter(d => ['accepted', 'paid', 'in_transit']?.includes(d?.status))?.length;
      const avgMatchScore = totalMatches > 0
        ? matchingLogs?.reduce((sum, log) => sum + (log?.match_score || 0), 0) / totalMatches
        : 0;

      setDashboardData({
        matchingLogs: toCamelCase(matchingLogs),
        emergencyEvents: toCamelCase(emergencyEvents),
        activeDeals: toCamelCase(activeDeals),
        flightInfo: toCamelCase(flightInfo),
        metrics: {
          successRate: successRate?.toFixed(1),
          pendingMatches,
          activePairings,
          avgMatchScore: avgMatchScore?.toFixed(2)
        }
      });
    } catch (error) {
      console.error('Load dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    const matchingLogsChannel = supabase
      ?.channel('ai-matching-logs-monitor')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ai_matching_logs'
        },
        () => {
          loadDashboardData();
        }
      )
      ?.subscribe();

    const emergencyEventsChannel = supabase
      ?.channel('emergency-events-monitor')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'emergency_rematch_events'
        },
        () => {
          loadDashboardData();
        }
      )
      ?.subscribe();

    const dealsChannel = supabase
      ?.channel('deals-monitor-dashboard')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deals'
        },
        () => {
          loadDashboardData();
        }
      )
      ?.subscribe();

    const flightChannel = supabase
      ?.channel('flight-monitor-dashboard')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'flight_information'
        },
        () => {
          loadDashboardData();
        }
      )
      ?.subscribe();

    setRealtimeChannels([matchingLogsChannel, emergencyEventsChannel, dealsChannel, flightChannel]);
  };

  const cleanupRealtimeSubscriptions = () => {
    realtimeChannels?.forEach(channel => {
      if (channel) {
        supabase?.removeChannel(channel);
      }
    });
    setRealtimeChannels([]);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toCamelCase = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj?.map(item => toCamelCase(item));
    const camelObj = {};
    for (const key in obj) {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      camelObj[camelKey] = toCamelCase(obj?.[key]);
    }
    return camelObj;
  };

  const translations = {
    en: {
      title: 'AI Monitoring Dashboard',
      subtitle: 'Real-time oversight of intelligent matching system',
      timeRange: 'Time Range',
      region: 'Region',
      confidence: 'Min Confidence',
      severity: 'Event Severity',
      allRegions: 'All Regions',
      allSeverity: 'All Severity',
      last1h: 'Last 1 Hour',
      last24h: 'Last 24 Hours',
      last7d: 'Last 7 Days',
      last30d: 'Last 30 Days',
      critical: 'Critical',
      moderate: 'Moderate',
      minor: 'Minor'
    },
    ko: {
      title: 'AI 모니터링 대시보드',
      subtitle: '지능형 매칭 시스템 실시간 감시',
      timeRange: '시간 범위',
      region: '지역',
      confidence: '최소 신뢰도',
      severity: '이벤트 심각도',
      allRegions: '모든 지역',
      allSeverity: '모든 심각도',
      last1h: '최근 1시간',
      last24h: '최근 24시간',
      last7d: '최근 7일',
      last30d: '최근 30일',
      critical: '긴급',
      moderate: '보통',
      minor: '경미'
    }
  };

  const t = translations?.[currentLanguage];

  const timeRangeOptions = [
    { value: '1h', label: t?.last1h },
    { value: '24h', label: t?.last24h },
    { value: '7d', label: t?.last7d },
    { value: '30d', label: t?.last30d }
  ];

  const severityOptions = [
    { value: '', label: t?.allSeverity },
    { value: 'critical', label: t?.critical },
    { value: 'moderate', label: t?.moderate },
    { value: 'minor', label: t?.minor }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Activity" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                  {t?.title}
                </h1>
                <p className="text-sm text-muted-foreground">{t?.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-elevation-md p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select
                label={t?.timeRange}
                options={timeRangeOptions}
                value={filters?.timeRange}
                onChange={(value) => handleFilterChange('timeRange', value)}
              />
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t?.confidence}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={filters?.confidenceThreshold}
                  onChange={(e) => handleFilterChange('confidenceThreshold', parseFloat(e?.target?.value))}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {(filters?.confidenceThreshold * 100)?.toFixed(0)}%
                </p>
              </div>
              <Select
                label={t?.severity}
                options={severityOptions}
                value={filters?.severity}
                onChange={(value) => handleFilterChange('severity', value)}
              />
              <div className="flex items-end">
                <button
                  onClick={loadDashboardData}
                  className="w-full h-10 px-4 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-base flex items-center justify-center gap-2"
                >
                  <Icon name="RefreshCw" size={16} />
                  <span className="text-sm font-medium">
                    {currentLanguage === 'en' ? 'Refresh' : '새로고침'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <MetricsPanel metrics={dashboardData?.metrics} currentLanguage={currentLanguage} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <MatchActivityFeed
              logs={dashboardData?.matchingLogs}
              currentLanguage={currentLanguage}
            />
            <FlightTrackingModule
              flightInfo={dashboardData?.flightInfo}
              currentLanguage={currentLanguage}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <EmergencyEventsPanel
              events={dashboardData?.emergencyEvents}
              currentLanguage={currentLanguage}
            />
            <AIReasoningLogs
              logs={dashboardData?.matchingLogs}
              currentLanguage={currentLanguage}
            />
          </div>
        </div>
      </main>

      <BottomTabNavigation />
    </div>
  );
};

export default AIMonitoringDashboard;